import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { viagensService, SugestaoDeLugar } from '../services/viagens';

/**
 * Campo de endereço com sugestões do mapa.
 *
 * Continua sendo um campo de texto: quem quiser digitar o endereço à mão digita,
 * e o cadastro salva do mesmo jeito. As sugestões são um atalho, não um portão —
 * numa instalação sem chave do Google configurada, ou com o Google fora do ar,
 * a lista simplesmente não aparece e ninguém fica impedido de trabalhar.
 *
 * Escolher da lista faz diferença numa coisa só: é o que entrega o `place_id`, e
 * sem ele não há como calcular a distância da rota.
 */

/**
 * Espera o gestor parar de digitar antes de perguntar ao mapa.
 *
 * Cada consulta é cobrada. Sem isto, "Macapá" custaria seis consultas — uma por
 * letra — e nenhuma das cinco primeiras seria útil.
 */
const ESPERA_ANTES_DE_BUSCAR = 400;

interface Props {
  label: string;
  name: string;
  value: string;
  onChange: (texto: string, placeId: string | null) => void;
  required?: boolean;
  placeholder?: string;
}

const CampoDeLugar: React.FC<Props> = ({ label, name, value, onChange, required, placeholder }) => {
  const [sugestoes, setSugestoes] = useState<SugestaoDeLugar[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [destacado, setDestacado] = useState(-1);

  // Guarda o que veio da lista para não buscar de novo logo depois de escolher:
  // escolher preenche o campo, e o campo preenchido dispararia outra busca.
  const escolhido = useRef<string | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const listaId = `${name}-sugestoes`;

  useEffect(() => {
    if (escolhido.current === value) return;

    if (value.trim().length < 3) {
      setSugestoes([]);
      setBuscando(false);

      return;
    }

    // O aviso liga AQUI, junto com a tecla, e não lá dentro depois da espera.
    // Entre digitar e a consulta sair há 400ms de propósito; sem aviso nenhum
    // nesse intervalo o gestor digita e a tela fica muda, e ele conclui que o
    // campo não faz nada.
    setBuscando(true);

    const timer = setTimeout(async () => {
      try {
        const resposta = await viagensService.buscarLugares(value);
        setSugestoes(resposta.data ?? []);
        setAberto((resposta.data ?? []).length > 0);
        setDestacado(-1);
      } catch {
        // Sem sugestão o campo continua sendo um campo de texto. Avisar de erro
        // aqui só atrapalharia quem está digitando o endereço à mão.
        setSugestoes([]);
      } finally {
        setBuscando(false);
      }
    }, ESPERA_ANTES_DE_BUSCAR);

    return () => clearTimeout(timer);
  }, [value]);

  // Clicar fora fecha a lista. Sem isto ela fica flutuando sobre o resto do
  // formulário enquanto o gestor preenche os outros campos.
  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      if (container.current && !container.current.contains(evento.target as Node)) {
        setAberto(false);
      }
    };

    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  const escolher = (sugestao: SugestaoDeLugar) => {
    escolhido.current = sugestao.descricao;
    onChange(sugestao.descricao, sugestao.place_id);
    setAberto(false);
    setSugestoes([]);
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (!aberto || sugestoes.length === 0) return;

    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setDestacado((i) => (i + 1) % sugestoes.length);
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setDestacado((i) => (i <= 0 ? sugestoes.length - 1 : i - 1));
    } else if (evento.key === 'Enter' && destacado >= 0) {
      // Só intercepta o Enter quando há item destacado: de outro modo o gestor
      // que digitou o endereço à mão não conseguiria enviar o formulário.
      evento.preventDefault();
      escolher(sugestoes[destacado]);
    } else if (evento.key === 'Escape') {
      setAberto(false);
    }
  };

  return (
    <div ref={container} className="relative">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && '*'}
      </label>

      <div className="relative">
        <input
          id={name}
          name={name}
          type="text"
          value={value}
          onChange={(e) => {
            escolhido.current = null;
            // Digitar depois de escolher invalida o `place_id`: o texto deixou
            // de ser o lugar que o mapa devolveu, e usar o id antigo calcularia
            // a distância de um endereço que não está mais escrito ali.
            onChange(e.target.value, null);
          }}
          onKeyDown={aoTeclar}
          onFocus={() => sugestoes.length > 0 && setAberto(true)}
          required={required}
          maxLength={200}
          autoComplete="off"
          role="combobox"
          aria-expanded={aberto}
          // Diz ao leitor de tela qual lista este campo comanda, e qual item
          // esta em foco enquanto se navega pelas setas. Sem isso ele anuncia o
          // campo e cala sobre as sugestoes.
          aria-controls={listaId}
          aria-activedescendant={destacado >= 0 ? `${listaId}-${destacado}` : undefined}
          aria-autocomplete="list"
          placeholder={placeholder}
          className="mt-1 block w-full px-3 py-2 pr-9 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />

        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
        </span>
      </div>

      {/* No mesmo lugar onde as sugestões vão aparecer, e não num canto: o olho
          já está ali esperando a lista. `aria-live` faz o leitor de tela
          anunciar sozinho, sem o usuário precisar procurar. */}
      {buscando && (
        <div
          aria-live="polite"
          className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2.5 flex items-center gap-2 text-sm text-gray-500"
        >
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          Buscando endereços...
        </div>
      )}

      {!buscando && aberto && sugestoes.length > 0 && (
        /* Rotulo proprio, e nao "Sugestoes para Origem": repetir o nome do
           campo faz o leitor de tela anunciar dois elementos com o mesmo nome,
           e quem navega por voz nao sabe qual esta ouvindo. */
        <ul
          id={listaId}
          role="listbox"
          aria-label="Sugestões de endereço"
          className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {sugestoes.map((sugestao, indice) => (
            <li key={sugestao.place_id}>
              <button
                type="button"
                id={`${listaId}-${indice}`}
                role="option"
                aria-selected={indice === destacado}
                onClick={() => escolher(sugestao)}
                onMouseEnter={() => setDestacado(indice)}
                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${
                  indice === destacado ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                {sugestao.descricao}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CampoDeLugar;
